import { ReactElement, createContext, useContext, useEffect, useState } from "react";
import UserModel from "../../model/user-model";
import { StorageKey } from "../../common/const/storage-key";
import { HttpBuilder } from "../../http/http_builder";
import { ApiPath } from "../../http/api-path";

const AuthContext = createContext<AuthType>(null!)

interface AuthType {
    user?: UserModel
    isAuthenticated: boolean
    isReady: boolean
    login: (user: UserModel) => void
    logout: () => void
    refreshMe: () => Promise<void>
}

export function useAuthProvider() {
    return useContext(AuthContext)
}

function readStoredUser(): UserModel | undefined {
    try {
        const raw = localStorage.getItem(StorageKey.user)
        if (!raw) return undefined
        return UserModel.fromJson(JSON.parse(raw))
    } catch {
        return undefined
    }
}

export function AuthProvider({ children }: { children: ReactElement }) {
    const [user, setUser] = useState<UserModel | undefined>(readStoredUser())
    const [isReady, setIsReady] = useState(false)

    function login(next: UserModel) {
        localStorage.setItem(StorageKey.user, JSON.stringify(next))
        setUser(next)
    }

    function logout() {
        localStorage.removeItem(StorageKey.user)
        setUser(undefined)
    }

    async function refreshMe(): Promise<void> {
        const current = readStoredUser()
        if (!current?.token) return

        const result = await HttpBuilder.main()
            .setPath(ApiPath.me)
            .setFromJson((json) => UserModel.fromJson(json))
            .get<UserModel>()

        if (!result.isOk() || !result.data) {
            if (result.code === 401 || result.code === 403) logout()
            return
        }

        const merged = UserModel.fromJson({ ...current, ...result.data, token: current.token })
        login(merged)
    }

    useEffect(() => {
        refreshMe().finally(() => setIsReady(true))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const value: AuthType = {
        user,
        isAuthenticated: !!user?.token,
        isReady,
        login,
        logout,
        refreshMe,
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}
